/**
 * Fire-and-forget Grimoire cross-project MAB observation push.
 *
 * Sends bandit observations to the Grimoire exchange so learnings from
 * all Dreadful Depths sessions are shared across the project ecosystem.
 * Never throws — telemetry loss is acceptable, correctness is not.
 *
 * Key resolution order (first non-empty wins):
 *   1. VITE_GRIMOIRE_KEY  — baked at build time by Bifrost Runner / CF Pages secret
 *   2. keyCache("grimoire") — dev override via Settings or .env.local
 *
 * This means every player session contributes to the shared MAB without
 * any visible key management. Rotate by updating the CF Pages secret and
 * redeploying — no code change required.
 */

import type { CallSite, LLMCallMetric } from "./router";
import { CALLSITE_WEIGHTS } from "./router";
import { armKey } from "./models";
import { keyCache } from "$lib/stores/keys";

const GRIMOIRE_URL = import.meta.env.VITE_GRIMOIRE_URL ?? "https://grimoire.mock1ng.workers.dev";

/** Build-time baked key — empty string if not set during CI. */
const GRIMOIRE_BUILTIN_KEY: string = import.meta.env.VITE_GRIMOIRE_KEY ?? "";

const GRIMOIRE_PROJECT = "dreadful-depths";
const GRIMOIRE_SUCCESS_THRESHOLD = 0.3;

/** Map CallSite to Grimoire canonical category. */
const CATEGORY_MAP: Record<CallSite, string> = {
  "narrative-turn": "narrative",
  "opening-scene": "narrative",
  "option-generation": "narrative",
  "background-generation": "narrative",
  "world-description": "narrative",
  "animation-frame": "narrative",
  "character-assignment": "analysis",
  "beat-extraction": "parsing",
  "arc-generation": "narrative",
  "memory-compression": "summarization",
  "beat-summary": "summarization",
};

/**
 * Resolve the Grimoire API key.
 *
 * Prefers the build-time constant so all player sessions contribute
 * telemetry without requiring manual key setup. Falls back to the
 * developer keyCache override for local testing or key rotation testing.
 */
async function resolveKey(): Promise<string | null> {
  if (GRIMOIRE_BUILTIN_KEY) return GRIMOIRE_BUILTIN_KEY;
  return keyCache.get("grimoire");
}

/**
 * Push a single observation to Grimoire. Non-blocking, fire-and-forget.
 *
 * Call this after every `recordObservation()`. Async key loading and
 * network errors are silently swallowed — telemetry loss is tolerable.
 */
export function pushGrimoireObservation(metric: LLMCallMetric): void {
  const category = CATEGORY_MAP[metric.callSite];
  if (!category) return;

  const key = armKey(metric.provider, metric.modelId);
  const weights = CALLSITE_WEIGHTS[metric.callSite];
  const reward =
    metric.scores.accuracy * weights.accuracy +
    metric.scores.completeness * weights.completeness +
    metric.scores.creativity * weights.creativity +
    metric.scores.speed * weights.speed +
    metric.scores.cost * weights.cost;

  const success = reward > GRIMOIRE_SUCCESS_THRESHOLD;

  void (async () => {
    try {
      const apiKey = await resolveKey();
      if (!apiKey) return;

      await fetch(`${GRIMOIRE_URL}/v1/observe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          observations: [
            {
              project: GRIMOIRE_PROJECT,
              arm_key: key,
              category,
              raw_reward: Math.max(0, Math.min(1, reward)),
              success,
              latency_ms: metric.latencyMs,
              timestamp: metric.timestamp,
            },
          ],
        }),
      });
    } catch {
      // telemetry loss is acceptable
    }
  })();
}
